import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { convertISODateToMMDDYYYY, convertISOToTime, getTimestampDisplay, stringToHexColor } from '@/lib/utils';
import { useUserStore } from '@/stores/user';
import type { WorkOrderCommentResponse } from '@/types/workOrder.types';
import { Dispatch, useEffect, useRef, useState } from 'react';
import Spinner from '@/images/spinner.svg';

const Comment = ({ comment }: { comment: WorkOrderCommentResponse }) => {
  const { timeDisplayText, hasTooltip } = getTimestampDisplay(comment.createdAt);
  const userStore = useUserStore((state) => state.user);
  return (
    <div key={comment.commentId} className={`p-4 border-bordercolor flex flex-col h-fit gap-2`}>
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <span
            className={`rounded-full w-[32px] h-[32px] text-lg focus:outline-none focus-visible:outline-none text-white p-2.5 items-center justify-center flex`}
            style={{ background: stringToHexColor(comment.addedBy.firstName) }}
          >
            {comment.addedBy.firstName?.charAt(0)}
          </span>
          <span className="text-base font-semibold">{`${comment.addedBy.firstName + ' ' + comment.addedBy.lastName} ${userStore?.userId === comment.addedById ? '(you)' : ''} `}</span>
        </div>
        {hasTooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="text-sm text-gray-500">{timeDisplayText}</span>
              </TooltipTrigger>
              <TooltipContent>
                <span>
                  {convertISODateToMMDDYYYY(comment.createdAt)} {convertISOToTime(comment.createdAt)}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-placeholder font-normal text-[13px]">{timeDisplayText}</span>
        )}
      </div>
      <div className="text-placeholder font-normal text-sm break-all">{comment.comment}</div>
    </div>
  );
};
const PeCommentsSection = ({
  comments,
  isFetching,
  setPage,
  hasMore,
  page,
}: {
  isFetching: boolean;
  comments: WorkOrderCommentResponse[];
  setPage: Dispatch<React.SetStateAction<number>>;
  hasMore: boolean;
  page: number;
}) => {
  const [lastCommentRef, setLastCommentRef] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lastElement, setLastElement] = useState<HTMLElement | null>(null);
  const [firstElement, setFirstElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (lastCommentRef) lastCommentRef?.scrollIntoView({ behavior: 'auto' });
  }, [lastCommentRef]);

  const observer = useRef(
    new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && (!isFetching || page == 0)) {
          setPage((prev) => prev + 1);
        }
      },
      {
        rootMargin: '50px',
        threshold: 1,
      }
    )
  );

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement) {
      if (isFetching || !hasMore) {
        currentObserver.disconnect();
      } else {
        currentObserver.observe(currentElement);
      }
    }
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement, isFetching, hasMore, comments]);

  useEffect(() => {
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      containerRef.current?.scrollBy(0, -20);
    }
  }, [comments.length]);

  return (
    <div className="flex flex-col h-full w-full">
      {isFetching && (
        <div className="w-full items-center flex justify-center">
          <img src={Spinner} alt="Loading..." className="h-[25px] w-[25px] animate-spin" />
        </div>
      )}

      {comments.length ? (
        <div className="overflow-auto h-[calc(100vh-245px)] custom-scrollbar" ref={containerRef}>
          {comments.map((comment, index) => {
            const isLast = index === comments.length - 1;
            return (
              <div
                key={comment.commentId}
                className="px-2 whitespace-pre-wrap"
                ref={
                  isLast
                    ? (el) => {
                        setLastCommentRef(el);
                      }
                    : index === 0
                      ? (el) => {
                          setLastElement(el);
                          setFirstElement(el);
                        }
                      : null
                }
              >
                <Comment comment={comment} />
                {!isLast && <hr />}
              </div>
            );
          })}
        </div>
      ) : !isFetching ? (
        <div className="w-full h-full flex justify-center items-center text-placeholder font-base"> No Results</div>
      ) : null}
    </div>
  );
};

export default PeCommentsSection;
